import { Form, Link, useLocation } from '@remix-run/react'
import { useEffect, useRef, useState } from 'react'
import {
	primaryCta,
	secondaryCta,
} from '#app/components/marketing/home/shared.tsx'
import { cn } from '#app/utils/misc.tsx'
import { useOptionalUser } from '#app/utils/user.ts'
import { Logo } from './logo.tsx'

const NAV_LINKS = [
	{ to: '/#features', label: 'Features' },
	{ to: '/#how', label: 'How it works' },
	{ to: '/calculator', label: 'Dosing calculator' },
	{ to: '/pricing', label: 'Plans' },
	{ to: '/#faq', label: 'FAQ' },
]

const focusRing =
	'focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-[3px] focus-visible:outline-cyan-300'

export const MarketingNav = () => {
	const location = useLocation()
	const user = useOptionalUser()
	const detailsRef = useRef<HTMLDetailsElement>(null)
	const summaryRef = useRef<HTMLElement>(null)
	const [open, setOpen] = useState(false)

	useEffect(() => {
		if (detailsRef.current?.open) detailsRef.current.open = false
	}, [location.pathname, location.hash])

	useEffect(() => {
		if (!open) return
		function onKey(event: KeyboardEvent) {
			if (event.key !== 'Escape') return
			if (detailsRef.current) detailsRef.current.open = false
			summaryRef.current?.focus()
		}
		function onPointerDown(event: PointerEvent) {
			const details = detailsRef.current
			if (!details || !(event.target instanceof Node)) return
			if (details.contains(event.target)) return
			details.open = false
		}
		document.addEventListener('keydown', onKey)
		document.addEventListener('pointerdown', onPointerDown)
		return () => {
			document.removeEventListener('keydown', onKey)
			document.removeEventListener('pointerdown', onPointerDown)
		}
	}, [open])

	return (
		<header className="sticky top-0 z-50 border-b border-slate-400/15 bg-[#020617]/95 backdrop-blur-md">
			<a
				href="#main"
				className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-[60] focus:rounded-lg focus:bg-cyan-300 focus:px-3.5 focus:py-2 focus:font-semibold focus:text-slate-950"
			>
				Skip to content
			</a>
			<nav
				aria-label="Main"
				className="mx-auto flex h-[68px] w-full max-w-[1160px] items-center justify-between gap-1.5 px-3 sm:gap-3 sm:px-6"
			>
				<Logo size="md" />
				<ul className="hidden items-center gap-6 lg:flex">
					{NAV_LINKS.map(link => (
						<li key={link.to}>
							<NavLink to={link.to} currentPath={location.pathname}>
								{link.label}
							</NavLink>
						</li>
					))}
				</ul>
				<div className="flex items-center gap-2 sm:gap-3">
					<AuthActions user={user} compact />
					<details
						ref={detailsRef}
						className="group lg:hidden"
						onToggle={(event) => setOpen(event.currentTarget.open)}
					>
						<summary
							ref={summaryRef}
							className={cn(
								'inline-flex h-11 w-11 cursor-pointer list-none items-center justify-center rounded-lg text-slate-200 hover:bg-white/5 [&::-webkit-details-marker]:hidden',
								focusRing,
							)}
						>
							<span className="sr-only">
								<span className="group-open:hidden">Open menu</span>
								<span className="hidden group-open:inline">Close menu</span>
							</span>
							<MenuIcon />
						</summary>
						<div
							id="mobile-nav"
							className="absolute inset-x-0 top-full border-t border-slate-400/15 bg-[#020617]"
						>
							<ul className="mx-auto flex w-full max-w-[1160px] flex-col gap-1 px-6 py-4">
								{NAV_LINKS.map(link => (
									<li key={link.to}>
										<MobileLink to={link.to} currentPath={location.pathname}>
											{link.label}
										</MobileLink>
									</li>
								))}
								<li className="mt-2 border-t border-slate-400/15 pt-3">
									<AuthActions user={user} stacked />
								</li>
							</ul>
						</div>
					</details>
				</div>
			</nav>
		</header>
	)
}

function NavLink({
	to,
	currentPath,
	children,
}: {
	to: string
	currentPath: string
	children: string
}) {
	const current = to === currentPath
	return (
		<Link
			to={to}
			aria-current={current ? 'page' : undefined}
			className={cn(
				'rounded-md text-[0.95rem] transition-colors hover:text-white',
				focusRing,
				current
					? 'font-semibold text-white underline decoration-2 underline-offset-4'
					: 'text-slate-300',
			)}
		>
			{children}
		</Link>
	)
}

function MobileLink({
	to,
	currentPath,
	children,
}: {
	to: string
	currentPath: string
	children: string
}) {
	const current = to === currentPath
	return (
		<Link
			to={to}
			aria-current={current ? 'page' : undefined}
			className={cn(
				'block rounded-lg px-3 py-3 text-slate-200 hover:bg-white/5',
				focusRing,
				current && 'font-semibold underline decoration-2 underline-offset-4',
			)}
		>
			{children}
		</Link>
	)
}

function AuthActions({
	user,
	compact = false,
	stacked = false,
}: {
	user: ReturnType<typeof useOptionalUser>
	compact?: boolean
	stacked?: boolean
}) {
	if (user) {
		return (
			<div
				className={cn(
					'flex items-center gap-2',
					stacked && 'flex-col items-stretch',
				)}
			>
				<Link
					to="/dashboard"
					className={cn(
						secondaryCta,
						'whitespace-nowrap !px-2 !py-2 text-sm',
						stacked && 'w-full !px-4',
					)}
				>
					Dashboard
				</Link>
				<Form
					method="POST"
					action="/logout"
					className={cn(
						'whitespace-nowrap',
						stacked ? 'w-full' : compact ? 'hidden lg:block' : undefined,
					)}
				>
					<button
						type="submit"
						className={cn(
							'whitespace-nowrap',
							stacked
								? cn(secondaryCta, 'w-full px-4 py-2 text-sm')
								: cn(
										'rounded-md px-2 py-2 text-[0.95rem] text-slate-300 hover:text-white',
										focusRing,
									),
						)}
					>
						Log out
					</button>
				</Form>
			</div>
		)
	}

	return (
		<div
			className={cn(
				'flex items-center gap-2',
				stacked && 'flex-col items-stretch gap-2',
			)}
		>
			<Link
				to="/login"
				className={cn(
					'rounded-md px-2 py-2 text-[0.95rem] text-slate-300 hover:text-white',
					focusRing,
					compact && 'hidden sm:inline',
					stacked && 'inline text-center',
				)}
			>
				Sign in
			</Link>
			<Link
				to="/signup"
				className={cn(
					primaryCta,
					'px-4 py-2 text-sm',
					compact && 'whitespace-nowrap px-3 sm:px-4',
					stacked && 'w-full',
				)}
			>
				Start free
			</Link>
		</div>
	)
}

function MenuIcon() {
	return (
		<>
			<svg
				width="22"
				height="22"
				viewBox="0 0 24 24"
				fill="none"
				stroke="currentColor"
				strokeWidth="2"
				strokeLinecap="round"
				className="group-open:hidden"
				aria-hidden="true"
			>
				<path d="M4 7h16M4 12h16M4 17h16" />
			</svg>
			<svg
				width="22"
				height="22"
				viewBox="0 0 24 24"
				fill="none"
				stroke="currentColor"
				strokeWidth="2"
				strokeLinecap="round"
				className="hidden group-open:block"
				aria-hidden="true"
			>
				<path d="M6 6l12 12M18 6L6 18" />
			</svg>
		</>
	)
}
